import { useEffect, useState } from 'react';

type Row = {
  number: string; extension: string; location: string; owner_type: string; owner_name: string;
  status: string; last_call_epoch: number | null; calls_90d: number | null; calls_180d: number | null; really_used: number | null;
};
type Resp = { count: number; rows: Row[]; summary: { status: string; n: number }[]; locations: string[] };

const N = (x: any) => (Number(x) || 0).toLocaleString();
const fdate = (e: number | null) => (e ? new Date(e * 1000).toISOString().slice(0, 10) : '—');
function Tile({ v, l }: { v: any; l: string }) {
  return <div className="card p-3"><div className="text-xl font-semibold">{N(v)}</div><div className="text-[11px] text-gray-500 capitalize">{l}</div></div>;
}
const badge = (s: string) => {
  const c = s === 'assigned' ? 'bg-green-100 text-green-700' : s === 'available' ? 'bg-blue-100 text-blue-700'
    : s === 'reserved' ? 'bg-amber-100 text-amber-700' : s === 'aging' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600';
  return <span className={`px-2 py-0.5 rounded text-[11px] ${c}`}>{s}</span>;
};

export default function CdrNumbers() {
  const [d, setD] = useState<Resp | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [location, setLocation] = useState('');
  const [owner, setOwner] = useState('');
  const [q, setQ] = useState('');
  const [idle, setIdle] = useState(false);
  const [next, setNext] = useState<{ number: string; location: string }[] | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function load() {
    setBusy(true); setErr(null);
    try {
      const p = new URLSearchParams();
      if (status) p.set('status', status);
      if (location) p.set('location', location);
      if (owner) p.set('owner', owner);
      if (q) p.set('q', q);
      if (idle) p.set('idle', '1');
      const r = await fetch('/cdr/api/numbers?' + p.toString(), { credentials: 'include' });
      const j = await r.json();
      if (!r.ok) { setErr(j.error || 'error'); setD(null); } else { setD(j); }
    } catch (e: any) { setErr(String(e)); }
    setBusy(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  async function findNext() {
    const p = new URLSearchParams({ count: '10' });
    if (location) p.set('location', location);
    const r = await fetch('/cdr/api/numbers/next?' + p.toString(), { credentials: 'include' });
    const j = await r.json();
    setNext(j.available || []);
  }
  async function act(path: string, body: any) {
    setMsg(null);
    const r = await fetch('/cdr/api/numbers/' + path, { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
    const j = await r.json();
    setMsg(r.ok ? `${path}: ${body.number} ok` : `error: ${j.error || r.status}`);
    load();
  }
  function dl() {
    if (!d) return;
    const cols = ['number', 'extension', 'location', 'owner_type', 'owner_name', 'status', 'last_call', 'calls_90d', 'calls_180d', 'really_used'];
    const rows = [cols.join(',')].concat(d.rows.map(r => [r.number, r.extension, r.location, r.owner_type, `"${(r.owner_name || '').replace(/"/g, '""')}"`, r.status, fdate(r.last_call_epoch), r.calls_90d || 0, r.calls_180d || 0, r.really_used ? 1 : 0].join(',')));
    const b = new Blob([rows.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = 'number_inventory.csv'; a.click();
  }

  const sum: Record<string, number> = {};
  (d?.summary || []).forEach(s => { sum[s.status] = s.n; });
  const total = Object.values(sum).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Tile v={total} l="in inventory" />
        <Tile v={sum['assigned'] || 0} l="assigned" />
        <Tile v={sum['available'] || 0} l="available" />
        <Tile v={sum['reserved'] || 0} l="reserved" />
        <Tile v={sum['aging'] || 0} l="aging" />
      </div>

      <div className="flex gap-2 items-center flex-wrap">
        <input placeholder="Number or ext" value={q} onChange={e => setQ(e.target.value)} className="border rounded-md px-2 py-1 text-xs bg-transparent" />
        <input placeholder="Owner (name/type)" value={owner} onChange={e => setOwner(e.target.value)} className="border rounded-md px-2 py-1 text-xs bg-transparent" />
        <select value={status} onChange={e => setStatus(e.target.value)} className="border rounded-md px-2 py-1 text-xs bg-transparent">
          <option value="">Any status</option><option>assigned</option><option>available</option><option>reserved</option><option>aging</option>
        </select>
        <select value={location} onChange={e => setLocation(e.target.value)} className="border rounded-md px-2 py-1 text-xs bg-transparent">
          <option value="">Any location</option>{(d?.locations || []).map(l => <option key={l}>{l}</option>)}
        </select>
        <label className="text-xs text-gray-500 flex items-center gap-1"><input type="checkbox" checked={idle} onChange={e => setIdle(e.target.checked)} /> Idle 180d+ (reclaim)</label>
        <button onClick={load} className="px-3 py-1.5 rounded-md bg-tnh-accent text-white text-xs">Search</button>
        <button onClick={findNext} className="px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800 text-xs">Next available</button>
        <button onClick={dl} className="px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800 text-xs">Export CSV</button>
        {busy && <span className="text-xs text-gray-500">Loading&hellip;</span>}
        {msg && <span className="text-xs text-gray-500">{msg}</span>}
      </div>

      {err && <div className="text-xs text-red-500">Sign in to the CDR portal to view ({err}).</div>}
      {next && <div className="card p-3 text-xs">Next available{location ? ` in ${location}` : ''}: {next.length ? next.map(n => n.number).join(', ') : 'none'}</div>}

      {d && (
        <div className="card p-3 overflow-auto">
          <table className="w-full text-xs">
            <thead className="text-gray-500"><tr>
              <th className="text-left px-2 py-1">Number</th><th className="text-left px-2 py-1">Ext</th><th className="text-left px-2 py-1">Location</th>
              <th className="text-left px-2 py-1">Owner</th><th className="text-left px-2 py-1">Status</th><th className="text-left px-2 py-1">Last call</th>
              <th className="text-right px-2 py-1">90d</th><th className="text-right px-2 py-1">180d</th><th className="text-left px-2 py-1">Used?</th><th className="px-2 py-1"></th>
            </tr></thead>
            <tbody>
              {d.rows.map(r => (
                <tr key={r.number} className="border-t border-gray-100 dark:border-gray-800">
                  <td className="px-2 py-1 font-mono">{r.number}</td>
                  <td className="px-2 py-1">{r.extension}</td>
                  <td className="px-2 py-1">{r.location}</td>
                  <td className="px-2 py-1">{r.owner_type ? `${r.owner_name || ''} (${r.owner_type})` : <span className="text-gray-400">unassigned</span>}</td>
                  <td className="px-2 py-1">{badge(r.status)}</td>
                  <td className="px-2 py-1">{fdate(r.last_call_epoch)}</td>
                  <td className="px-2 py-1 text-right">{N(r.calls_90d)}</td>
                  <td className="px-2 py-1 text-right">{N(r.calls_180d)}</td>
                  <td className="px-2 py-1">{r.really_used ? <span className="text-green-600">yes</span> : <span className="text-gray-400">no</span>}</td>
                  <td className="px-2 py-1 whitespace-nowrap">
                    {r.status === 'available' && <button onClick={() => act('reserve', { number: r.number })} className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-[11px]">Reserve</button>}
                    {(r.status === 'reserved' || r.status === 'aging') && <button onClick={() => act('release', { number: r.number, age_days: 0 })} className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-[11px]">Release</button>}
                    {r.status === 'assigned' && <button onClick={() => act('release', { number: r.number })} className="px-2 py-0.5 rounded bg-purple-100 text-purple-700 text-[11px]" title="Mark aging (does not unassign in Webex)">Age</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[11px] text-gray-400 mt-2">{d.count} shown · Reserve/Age update inventory status only (reversible); they do not change Webex assignment.</div>
        </div>
      )}
    </div>
  );
}
